import { apiFetch, fetchWithAuth } from "@/api/client";
import { attemptRefresh } from "@/auth/flow";
import { config } from "@/config";

/**
 * The lab's original attachment in the review view (Gate B M2 S16).
 *
 * Its own module rather than more of `@/api/portal`, so a test that mocks the portal module
 * does not have to know these exist. Both routes exist only with
 * SR_PORTAL_REPORT_ATTACHMENTS_ENABLED on, and both read the report through the same
 * own-lab check as the review view itself: another lab's report is a 404.
 */

/** One attachment as the BFF lists it: a position and what it is. No title, no key. */
export interface PortalReportAttachment {
  /** 1-based, the handle {@link openPortalReportAttachment} takes. */
  index: number;
  content_type: string;
  /** The lab marked it as its original report. */
  original: boolean;
  size: number;
}

export interface PortalReportAttachments {
  report_id: string;
  attachments: PortalReportAttachment[];
}

function attachmentsPath(reportId: string): string {
  return `/api/v1/portal/reports/${encodeURIComponent(reportId)}/attachments`;
}

/** `GET .../attachments`: what the lab sent with this report. Not an audited view. */
export function listPortalReportAttachments(reportId: string): Promise<PortalReportAttachments> {
  return apiFetch<PortalReportAttachments>(attachmentsPath(reportId));
}

/**
 * `GET .../attachments/{index}`: the bytes, as a Blob. Every successful call is recorded
 * server-side as a `report.viewed` event naming the attachment.
 *
 * The Blob's type is the server's for a PDF and `application/octet-stream` for anything
 * else, whatever type the lab declared, so nothing the lab sent can become a page of ours
 * when it is opened from an object URL on this origin.
 */
export async function fetchPortalReportAttachment(reportId: string, index: number): Promise<Blob> {
  const url = `${config.apiBaseUrl}${attachmentsPath(reportId)}/${index}`;
  let resp = await fetchWithAuth(url);
  if (resp.status === 401 && (await attemptRefresh())) {
    resp = await fetchWithAuth(url);
  }
  if (!resp.ok) {
    throw new Error(`API request failed: ${resp.status} ${resp.statusText}`);
  }
  const bytes = await resp.arrayBuffer();
  const type = resp.headers.get("content-type")?.split(";")[0]?.trim().toLowerCase();
  return new Blob([bytes], { type: type === PDF ? PDF : "application/octet-stream" });
}

export const PDF = "application/pdf";
